// Template → document (M14), and the two edits every ad needs: apply the brand, set the ad's fields. Both
// rewrite the affected nodes (resolved text, brand fonts, touched appearances) so the display list redraws
// them; colours follow the brand through the global swatches 'brand-<role>'.
import type { AdData, BrandData, Doc, ImageNode, Node, TextNode } from '../model/types';
import { boundText, SAMPLE_AD } from '../ad/fields';
import { nearestWeight } from '../text/fontCatalog';
import {
  Builder,
  BRAND_ROLES,
  TEXT_SWATCH,
  TONE_SWATCH,
  type TemplateDef,
  type TextRoleColor,
  type ToneRole,
} from './dsl';
import { pickText } from '../ad/contrast';
import { DEFAULT_BRAND } from '../ad/brand';
import { ALL_TEMPLATES } from './catalog';
import { cmd } from '../commands/commands';
import type { Command } from '../commands/history';

export const templateById = (id: string) => ALL_TEMPLATES.find((t) => t.id === id);

export function walk(doc: Doc, fn: (n: Node) => void) {
  const visit = (n: Node) => {
    fn(n);
    if (n.kind === 'group') n.children.forEach(visit);
  };
  for (const l of doc.layers) l.children.forEach(visit);
}
export const textNodes = (doc: Doc) => {
  const out: TextNode[] = [];
  walk(doc, (n) => n.kind === 'text' && out.push(n));
  return out;
};
export const imageNodes = (doc: Doc, role?: 'photo' | 'logo') => {
  const out: ImageNode[] = [];
  walk(doc, (n) => n.kind === 'image' && (!role || n.role === role) && out.push(n));
  return out;
};

const mergeAd = (base: AdData, p?: Partial<AdData>): AdData => ({
  ...base,
  ...p,
  items: p?.items ?? base.items,
});

/** Applies brand + ad to a (draft) document in place. */
export function applyBrandAndAd(d: Doc, brand: BrandData, ad: AdData) {
  d.brand = brand;
  d.ad = ad;
  for (const r of BRAND_ROLES) {
    const sw = d.swatches.find((s) => s.id === `brand-${r}`);
    if (sw) sw.color = { rgb: [...brand.colors[r]] as [number, number, number], alpha: 1 };
  }
  deriveTextColors(d);
  walk(d, (n) => {
    // A new appearance object makes every node redraw with the new swatch colours.
    n.appearance = { ...n.appearance };
    if (n.kind === 'text') {
      if (n.style.fontRole) {
        const want = n.style.fontRole === 'heading' ? brand.headingFont : brand.bodyFont;
        const f = nearestWeight(want, n.style.weight);
        n.style = { ...n.style, family: f.family, weight: f.weight };
      }
      n.text = boundText(n, ad, brand);
    }
    if (n.kind === 'image' && n.role === 'logo') {
      // A logo on a light background uses the brand's light-background logo when there is one.
      if (n.onLight && brand.logoLightAsset) {
        n.assetId = brand.logoLightAsset;
        n.iw = brand.logoLightW ?? 0;
        n.ih = brand.logoLightH ?? 0;
        return;
      }
      n.assetId = brand.logoAsset;
      n.iw = brand.logoW;
      n.ih = brand.logoH;
    }
  });
  if (d.artboards[0]?.background) d.artboards[0] = { ...d.artboards[0] };
}

/** A new document from a template, with the brand and the template's sample content. */
export function instantiate(t: TemplateDef, brand: BrandData, ad?: AdData): Doc {
  const b = new Builder(t.format);
  // M20: no text under 3% of the short side (the Classic set keeps its own sizes).
  if (t.family && t.family !== 'classic') b.minText = Math.ceil(0.03 * Math.min(b.W, b.H));
  t.build(b);
  const doc = b.doc(t.name.es);
  doc.template = t.id;
  // M21b: a restaurant other than the built-in brand starts with the "Any food" sample texts.
  const sample = brand.name === DEFAULT_BRAND.name ? t.ad : { ...t.ad, ...t.adAny };
  applyBrandAndAd(doc, brand, mergeAd(ad ?? SAMPLE_AD, ad ? undefined : sample));
  return doc;
}

/** Command: apply a (new) brand to the open ad. One undo step. */
export const applyBrand = (brand: BrandData): Command =>
  cmd('cmdApplyBrand', (d) => applyBrandAndAd(d, brand, d.ad ?? SAMPLE_AD));

/** Command: change the ad's fields (typing in the form merges into one undo step per field). */
export const setAd = (fn: (a: AdData) => void, merge?: string): Command =>
  cmd(
    'cmdEditAd',
    (d) => {
      if (!d.ad) d.ad = structuredClone(SAMPLE_AD);
      fn(d.ad);
      walk(d, (n) => {
        if (n.kind === 'text' && n.bind && n.bind.role !== 'free') {
          const t = boundText(n, d.ad, d.brand);
          if (t !== n.text) n.text = t;
        }
      });
    },
    merge,
  );

/**
 * Text colours from the brand that meet WCAG AA on their background (see TEXT_SWATCH in dsl.ts). Brand values
 * never change; only which of them a text uses. Candidates in order of preference: brand colours first,
 * white / black only when no brand colour is readable.
 */
export function deriveTextColors(d: Doc) {
  const rgb = (id: string) => d.swatches.find((s) => s.id === id)?.color.rgb as RGB | undefined;
  const primary = rgb('brand-primary'),
    secondary = rgb('brand-secondary'),
    text = rgb('brand-text'),
    accent = rgb('brand-accent');
  if (!primary || !secondary || !text || !accent) return;
  const light = rgb(TONE_SWATCH.toneLight) ?? WHITE;
  const pick: Record<TextRoleColor, RGB> = {
    onPrimary: pickText([WHITE, text, secondary], [primary], 3),
    onPrimarySmall: pickText([WHITE, text, secondary, BLACK], [primary], 4.5),
    onSecondary: pickText([WHITE, text, primary], [secondary], 3),
    onSecondarySmall: pickText([WHITE, text, primary, BLACK], [secondary], 4.5),
    accentText: pickText([secondary, primary, accent, text, BLACK], [WHITE, light], 4.5),
    accentOnSecondary: pickText([primary, accent, WHITE], [secondary], 3),
  };
  for (const r of Object.keys(TEXT_SWATCH) as TextRoleColor[]) {
    const sw = d.swatches.find((s) => s.id === TEXT_SWATCH[r]);
    if (sw) sw.color = { rgb: [...pick[r]] as RGB, alpha: 1 };
  }
}
type RGB = [number, number, number];
const WHITE: RGB = [1, 1, 1],
  BLACK: RGB = [0, 0, 0];

/** M20: sets the photo-tone swatches (when the document has them) and redraws everything that may use them. */
export function setTones(d: Doc, tone: Record<ToneRole, [number, number, number]>) {
  let any = false;
  for (const r of Object.keys(TONE_SWATCH) as ToneRole[]) {
    const sw = d.swatches.find((s) => s.id === TONE_SWATCH[r]);
    if (sw) {
      sw.color = { rgb: [...tone[r]] as [number, number, number], alpha: 1 };
      any = true;
    }
  }
  if (!any) return;
  deriveTextColors(d);
  walk(d, (n) => void (n.appearance = { ...n.appearance }));
  if (d.artboards[0]?.background) d.artboards[0] = { ...d.artboards[0] };
}

/**
 * Command: put an image into a slot (photo or logo); a photo's tone recolours the M20 templates. M21: a photo
 * goes into every photo slot of the ad (the bite template shows the same dish twice), and a slot with a
 * template crop gets that crop back. M21b: a slot marked `own` (a second dish) is filled only by itself.
 */
export const fillSlot = (
  id: string,
  asset: { id: string; w: number; h: number },
  photoKey?: string,
  tone?: Record<ToneRole, [number, number, number]> | null,
): Command =>
  cmd('cmdFillPhoto', (d) => {
    let shared = false;
    walk(
      d,
      (n) => void (n.id === id && n.kind === 'image' && n.role === 'photo' && !n.own && (shared = true)),
    );
    walk(d, (n) => {
      if (n.kind === 'image' && (n.id === id || (shared && n.role === 'photo' && !n.own))) {
        n.assetId = asset.id;
        n.iw = asset.w;
        n.ih = asset.h;
        if (n.crop) Object.assign(n, n.crop);
        else {
          // M20 floating cut-outs are drawn smaller than their frame (room for the shadow): keep that size.
          n.zoom = n.zoom < 1 ? n.zoom : 1;
          n.offsetX = 0;
          n.offsetY = 0;
        }
        n.photoKey = photoKey;
      }
    });
    if (tone) setTones(d, tone);
  });
